"use client";
import {
  Checkbox,
  Input,
  Select,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useContext, useEffect, useState } from "react";
import { GetWorkHistory } from "@/actions/templates/WorkHistory/GetWorkHistory";
import moment from "moment";
import HistorySection from "@/components/dashboard/history/HistorySection";
import HistoryRecordsTable from "@/components/dashboard/HistoryRecordsTable";
import {
  EVENT_LIBRARY,
  FULL_DATE_FORMAT,
  HISTORY_TEMPLATES,
  REQUIRED_FIELD,
} from "@/lib/constants";
import {
  PostWorkHistory,
  WorkHistoryPayload,
} from "@/actions/templates/WorkHistory/PostWorkHistory";
import { PutWorkHistory } from "@/actions/templates/WorkHistory/PutWorkHistory";
import HistoryModal from "@/components/dashboard/history/HistoryModal";
import { DeleteWorkHistory } from "@/actions/templates/WorkHistory/DeleteWorkHistory";
import { useForm } from "react-hook-form";
import FormRow from "@/components/dashboard/application/FormRow";
import { AuthContext } from "@/providers/auth";
import useAnalytics from "@/lib/hooks/useAnalytics";
import handleServerAction from "@/lib/hooks/handleServerAction";
import { useIsTextMasked } from "../children/useIsDataMasked";
import { InputWrapper } from "@/components/forms/JailTextInputWrapper";

const category = "Work";
const defaultValues = HISTORY_TEMPLATES[category].defaultValues;

const WorkHistoryPage = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { getUserDetails } = useContext(AuthContext);
  const toast = useToast();
  const track = useAnalytics();
  const [isLoading, setIsLoading] = useState(true);
  const [records, setRecords] = useState<any[]>([]);

  const {
    handleSubmit,
    register,
    reset,
    control,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<WorkHistoryPayload>({
    defaultValues: defaultValues,
  });

  const refreshState = async () => {
    onClose();
    await getRecords();
    await getUserDetails();
    reset(defaultValues);
  };

  const onAdd = async (payload: WorkHistoryPayload) => {
    try {
      await handleServerAction(PostWorkHistory, {
        ...payload,
        EmployedTo: payload.IsCurrentlyWorking ? null : payload.EmployedTo,
      });
      await refreshState();

      toast({
        status: "success",
        title: "Record Added",
        description: "Record has been successfully added.",
      });

      track(EVENT_LIBRARY.ADDED_HISTORY, {
        category,
      });
    } catch (err) {
      toast({
        status: "error",
        title: "Error Occured",
        description: err.message,
      });
    }
  };

  const onUpdate = async (payload: WorkHistoryPayload) => {
    try {
      await handleServerAction(PutWorkHistory, {
        ...payload,
        EmployedTo: payload.IsCurrentlyWorking ? null : payload.EmployedTo,
      });
      await refreshState();
      toast({
        status: "success",
        title: "Record Updated",
        description: "Record has been successfully updated.",
      });

      track(EVENT_LIBRARY.UPDATED_HISTORY, {
        category,
      });
    } catch (err) {
      toast({
        status: "error",
        title: "Error Occured",
        description: err.message,
      });
    }
  };

  const onEdit = (id: number) => {
    const editRecord = records.find((record) => record.id === id);
    if (editRecord) {
      reset({
        Id: editRecord?.id,
        CompanyName: editRecord?.companyName,
        CompanyAddress: editRecord?.companyAddress,
        AddressTwo: editRecord?.addressTwo,
        Zip: editRecord?.zip,
        JobTitle: editRecord?.jobTitle,
        EmployedFrom: moment(editRecord?.employedFrom).format("YYYY-MM-DD"),
        EmployedTo: moment(editRecord?.employedTo).format("YYYY-MM-DD"),
        JobDuties: editRecord?.jobDuties,
        IsCurrentlyWorking: editRecord?.isCurrentlyWorking,
        HoursPerDay: editRecord?.hoursPerDay,
        DaysPerWeek: editRecord?.daysPerWeek,
        PayAmount: editRecord?.payAmount,
        PayFrequency: editRecord?.payFrequency,
        IsSelfEmployed: editRecord?.isSelfEmployed,
      });
      onOpen();
    }
  };

  const getRecords = async () => {
    setIsLoading(true);
    const data = await handleServerAction(GetWorkHistory);
    setRecords(data);
    setIsLoading(false);
  };

  useEffect(() => {
    getRecords();
  }, []);

  const onClick = () => {
    reset(defaultValues);
    onOpen();
  };

  const Id = watch("Id");
  const IsCurrentlyWorking = watch("IsCurrentlyWorking");
  const isTextMasked = useIsTextMasked() && Id;
  return (
    <HistorySection category={category} isLoading={isLoading} onClick={onClick}>
      <HistoryRecordsTable
        category={category}
        records={records}
        cells={records.map((record) => [
          record.companyName,
          record.jobTitle,
          moment(record.employedFrom).format("MMMM YYYY"),
          record.employedTo
            ? moment(record.employedTo).format("MMMM YYYY")
            : "",
        ])}
        onEdit={onEdit}
        deleteAction={DeleteWorkHistory}
        getRecords={getRecords}
      />
      <HistoryModal
        category={category}
        onAdd={handleSubmit(onAdd)}
        onUpdate={handleSubmit(onUpdate)}
        mode={Id ? "Update" : "Add"}
        isOpen={isOpen}
        onClose={onClose}
        isSubmitting={isSubmitting}
      >
        <FormRow label="Company Name" size="sm" fieldError={errors.CompanyName}>
          <InputWrapper
            name="CompanyName"
            control={control}
            rules={{ required: REQUIRED_FIELD }}
            placeholder="Mindset, Inc."
            maskInputField={isTextMasked}
          />
        </FormRow>

        {/* <FormRow
          label="Company Address"
          size="sm"
          fieldError={errors.CompanyAddress}
        >
          <Controller
            name="CompanyAddress"
            control={control}
            rules={{ required: REQUIRED_FIELD }}
            render={({ field }) => (
              <GoogleAddressInput
                value={field.value}
                onChange={field.onChange}
                prefillFields={["Zip"]}
                setValue={setValue}
              />
            )}
          />
        </FormRow>
        <FormRow
          label="Address Line 2 (Optional)"
          size="sm"
          fieldError={errors.AddressTwo}
        >
          <InputWrapper
            name="AddressTwo"
            control={control}
            placeholder="(Optional)"
            maskInputField={isTextMasked}
          />
        </FormRow>
        <FormRow label="Zip Code" size="sm" fieldError={errors.Zip}>
          <ZipCodeInput register={register} />
        </FormRow> */}
        <FormRow label="Job Title" size="sm" fieldError={errors.JobTitle}>
          <InputWrapper
            name="JobTitle"
            control={control}
            rules={{ required: REQUIRED_FIELD }}
            placeholder="i.e. Assistant"
            maskInputField={isTextMasked}
          />
        </FormRow>

        {/* <FormRow
          label="Self-Employed"
          size="sm"
          fieldError={errors.IsSelfEmployed}
        >
          <Checkbox {...register("IsSelfEmployed")} />
        </FormRow> */}
        <FormRow label="Start Date" size="sm" fieldError={errors.EmployedFrom}>
          <Input
            type="date"
            placeholder={FULL_DATE_FORMAT}
            textTransform="uppercase"
            {...register("EmployedFrom", { required: REQUIRED_FIELD })}
          />
        </FormRow>
        <FormRow
          label="Currently Working"
          size="sm"
          fieldError={errors.IsCurrentlyWorking}
        >
          <Checkbox {...register("IsCurrentlyWorking")} />
        </FormRow>
        {!IsCurrentlyWorking && (
          <FormRow label="End Date" size="sm" fieldError={errors.EmployedTo}>
            <Input
              type="date"
              placeholder={FULL_DATE_FORMAT}
              textTransform="uppercase"
              {...register("EmployedTo", { required: REQUIRED_FIELD })}
            />
          </FormRow>
        )}

        {/* <FormRow label="Job Duties" size="sm" fieldError={errors.JobDuties}>
          <InputWrapper
            name="JobDuties"
            control={control}
            rules={{ required: REQUIRED_FIELD }}
            placeholder="i.e. Organized Events"
            maskInputField={isTextMasked}
          />
        </FormRow> */}
        <FormRow
          label="Hours Per Day"
          size="sm"
          fieldError={errors.HoursPerDay}
        >
          <Input
            type="text"
            placeholder="8"
            {...register("HoursPerDay", { required: REQUIRED_FIELD })}
          />
        </FormRow>
        <FormRow
          label="Days Per Week"
          size="sm"
          fieldError={errors.DaysPerWeek}
        >
          <Input
            type="text"
            placeholder="5"
            {...register("DaysPerWeek", { required: REQUIRED_FIELD })}
          />
        </FormRow>
        <FormRow label="Pay Amount" size="sm" fieldError={errors.PayAmount}>
          <Input
            type="text"
            placeholder="$0.00"
            {...register("PayAmount", { required: REQUIRED_FIELD })}
          />
        </FormRow>
        <FormRow
          label="Pay Frequency"
          size="sm"
          fieldError={errors.PayFrequency}
        >
          <Select {...register("PayFrequency", { required: REQUIRED_FIELD })}>
            <option value="">Choose One</option>
            <option value="Hourly">Hourly</option>
            <option value="Daily">Daily</option>
            <option value="Bi-Weekly">Bi-Weekly</option>
            <option value="Weekly">Weekly</option>
            <option value="Monthly">Monthly</option>
            <option value="Yearly">Yearly</option>
          </Select>
        </FormRow>
      </HistoryModal>
    </HistorySection>
  );
};

export default WorkHistoryPage;
